var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useStripes } from '@folio/stripes/core';
import { MultiColumnList, Button, PaneMenu, Headline, Layout, } from '@folio/stripes/components';
import { VOCABULARY_TYPE_CONFIG, } from '../types/api';
import SearchLayout from '../components/SearchLayout';
import PaneHelpButton from '../components/PaneHelpButton';
import ScreenHelp from '../components/ScreenHelp';
import { VOCABULARIES_HELP } from '../components/screenHelpContent';
import useVocabularyList from '../hooks/useVocabularyList';
import { useVocabularyTypeOptions } from '../hooks/useVocabularyTypeOptions';
import useRecordDetail from '../hooks/useRecordDetail';
import RecordDetail from '../components/RecordDetail/RecordDetail';
import VocabularyForm from '../components/VocabularyForm';
import { TableLoadingSkeleton, TableRefetchOverlay } from '../components/TableStates';
var VocabulariesList = function (_a) {
    var _b;
    var match = _a.match, selectedId = _a.selectedId;
    var intl = useIntl();
    var history = useHistory();
    var stripes = useStripes();
    var queryClient = useQueryClient();
    var list = useVocabularyList();
    // CZNA-312 / CZNA-1470 — the type filter lists every registry type, including one
    // created in Nastavení → Slovníky; the hardcoded config only supplies fallback labels.
    var _c = useVocabularyTypeOptions(), typeFilterOptions = _c.options, labelOf = _c.labelOf, typeOf = _c.typeOf;
    var _d = useState(false), showCreateForm = _d[0], setShowCreateForm = _d[1];
    var _f = useState(null), editItem = _f[0], setEditItem = _f[1];
    var handleFormSuccess = useCallback(function () {
        queryClient.invalidateQueries('czna-vocabularies');
        setShowCreateForm(false);
        setEditItem(null);
    }, [queryClient]);
    var showCodeColumn = useMemo(function () {
        if (!list.typeFilter)
            return true;
        var registryType = typeOf(list.typeFilter);
        if (registryType)
            return registryType.codeRequired;
        var config = VOCABULARY_TYPE_CONFIG[list.typeFilter];
        return (config === null || config === void 0 ? void 0 : config.codeRequired) !== false;
    }, [list.typeFilter, typeOf]);
    var visibleColumns = useMemo(function () {
        var cols = ['name', 'type', 'status'];
        if (showCodeColumn) {
            cols.splice(1, 0, 'code');
        }
        return cols;
    }, [showCodeColumn]);
    var columnMapping = useMemo(function () { return ({
        name: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.list.name' }),
        code: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.list.code' }),
        type: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.list.type' }),
        status: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.list.status' }),
    }); }, [intl]);
    var columnWidths = useMemo(function () { return ({
        name: '40%',
        code: '15%',
        type: '25%',
        status: '20%',
    }); }, []);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    var formatter = useMemo(function () { return ({
        name: function (item) { return item.name; },
        code: function (item) { return item.code || ''; },
        type: function (item) { return labelOf(item.type); },
        status: function (item) { return (React.createElement("span", { style: { color: item.status === 'active' ? '#2b7d2b' : '#999' } }, item.status === 'active'
            ? intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.status.active' })
            : intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.status.inactive' }))); },
    }); }, [intl, labelOf]);
    var handleRowClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    function (_e, row) {
        history.push("".concat(match.path, "/").concat(row.id));
    }, [match.path, history]);
    var typeTabs = (React.createElement("div", { style: { marginBottom: '8px' } },
        React.createElement(Button, { key: "all", buttonStyle: !list.typeFilter ? 'primary' : 'default', onClick: function () { return list.handleTypeFilterChange(''); }, fullWidth: true, style: { justifyContent: 'flex-start' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.nav.filter.all" })),
        typeFilterOptions.map(function (option) { return (React.createElement(Button, { key: option.value, buttonStyle: list.typeFilter === option.value ? 'primary' : 'default', onClick: function () { return list.handleTypeFilterChange(option.value); }, fullWidth: true, style: { justifyContent: 'flex-start' } }, option.label)); })));
    // CZNA-804 — usage is per vocabulary, so the entry point appears once one is picked.
    var canSeeUsage = stripes.hasPerm('czna-authorities.vocabularies.usage.get');
    var lastMenu = (React.createElement(PaneMenu, null,
        React.createElement(PaneHelpButton, null,
            React.createElement(ScreenHelp, { spec: VOCABULARIES_HELP })),
        list.typeFilter && canSeeUsage && (React.createElement(Button, { marginBottom0: true, onClick: function () { return history.push("".concat(match.path, "/usage/").concat(encodeURIComponent(list.typeFilter))); }, "data-testid": "vocabulary-usage-open" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularies.usage.open" }))),
        React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, onClick: function () { return setShowCreateForm(true); } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularies.form.create" }))));
    // Detail pane (right panel) — reuses the same RecordDetail as authorities
    var detailRecord = useRecordDetail(selectedId).record;
    var preferredHeading = (_b = detailRecord === null || detailRecord === void 0 ? void 0 : detailRecord.headings) === null || _b === void 0 ? void 0 : _b.find(function (h) { return h.headingType === 'PREFERRED'; });
    var paneDetailTitle = (preferredHeading === null || preferredHeading === void 0 ? void 0 : preferredHeading.headingValue)
        || intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.detail.title' });
    // CZNA-1456 — the detail brings its own Pane, so its actions can sit in the header.
    var detailPane = selectedId ? (React.createElement(RecordDetail, { id: selectedId, onClose: function () { return history.push(match.path); }, paneProps: {
            defaultWidth: '44%',
            paneTitle: paneDetailTitle,
            paneSub: detailRecord
                ? "".concat(labelOf(detailRecord.type), " \u00B7 ").concat(detailRecord.authorityId)
                : undefined,
        } })) : undefined;
    return (React.createElement(React.Fragment, null,
        React.createElement(SearchLayout, { list: {
                filterPaneIsVisible: list.filterPaneIsVisible,
                toggleFilterPane: list.toggleFilterPane,
                setQuery: list.setQuery,
                handleClearSearch: list.handleClearSearch,
                query: list.query,
                totalRecords: list.totalRecords,
            }, searchPlaceholder: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.search.placeholder' }), paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularies.title" }), lastMenu: lastMenu, subNavigation: typeTabs, detailPane: detailPane }, (list.isLoading || list.isFetching) && !list.items.length ? (React.createElement(TableLoadingSkeleton, { testId: "vocabularies-loading", columns: [5, 5, 3], rows: 6 })) : list.items.length === 0 ? (React.createElement(Layout, { className: "display-flex flex-direction-column centerContent" },
            React.createElement(Layout, { className: "padding-all-gutter" },
                React.createElement(Headline, { size: "medium", margin: "none" },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.vocabularies.list.empty" }))))) : (
        /* CZNA-538 — see AuthoritiesList: the overlay replaces MCL's corner spinner. */
        React.createElement(TableRefetchOverlay, { active: list.isFetching, testId: "vocabularies-refetching" },
            React.createElement(MultiColumnList, __assign({ contentData: list.items, visibleColumns: visibleColumns, columnMapping: columnMapping, columnWidths: columnWidths, formatter: formatter, selectedRow: selectedId ? { id: selectedId } : undefined }, (list.typeFilter ? {
                sortDirection: list.sortOrder === 'desc' ? 'descending' : 'ascending',
                sortedColumn: list.sortBy,
                onHeaderClick: list.handleSort,
                nonInteractiveHeaders: ['type', 'status'],
            } : {
                nonInteractiveHeaders: ['name', 'code', 'type', 'status'],
            }), { pageAmount: list.pageSize, totalCount: list.totalRecords, onRowClick: handleRowClick }))))),
        showCreateForm && (React.createElement(VocabularyForm, { onClose: function () { return setShowCreateForm(false); }, onSuccess: handleFormSuccess, defaultType: list.typeFilter || undefined, onAdvancedEdit: function (_id, type) {
                // TASK-071: hand off to the full-pane RecordForm route. The modal
                // disables the button until a type is picked, so `type` is always
                // a non-empty value here.
                setShowCreateForm(false);
                history.push("".concat(match.path, "/new?type=").concat(encodeURIComponent(type)));
            } })),
        editItem && (React.createElement(VocabularyForm, { vocabulary: editItem, onClose: function () { return setEditItem(null); }, onSuccess: handleFormSuccess, onAdvancedEdit: function (vocabularyId) {
                // TASK-071: navigate to /:id/edit. Guard against the (unlikely)
                // undefined-id case for the edit modal — the type is inherent to
                // the existing record, so no query param is needed.
                setEditItem(null);
                if (vocabularyId) {
                    history.push("".concat(match.path, "/").concat(vocabularyId, "/edit"));
                }
            } }))));
};
export default VocabulariesList;
